'use client';

import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  zodResolver,
  Box,
  Button,
  Checkbox,
  Heading,
  HStack,
  Icon,
  Input,
  Text,
  VStack,
  toaster,
} from '@axon/ui';
import { z } from 'zod';
import { useTranslations } from 'next-intl';
import { FiMail, FiLock, FiLogIn, FiUser, FiTruck, FiSettings, FiShield } from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
import { useQuery } from '@tanstack/react-query';

import { ClientOnly } from '@/components/client-only';
import { Link } from '@/i18n/navigation';
import { useAuth } from '@/hooks/use-auth';
import { apiClient } from '@/lib/api-client';

// Fallback when the dev endpoint omits a per-user password. Seeded passwords
// are NOT uniform (demo@axonfleet.com differs), so prefer
// `DevUser.quickLoginPassword` from /auth/dev/quick-login-options.
const DEMO_QUICK_LOGIN_PASSWORD = 'demo123';

interface DevUser {
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  /** Seeder-resolved dev password for this user (dev-only endpoint). */
  quickLoginPassword?: string;
}

interface DevTenantOption {
  tenantId: string;
  subdomain: string;
  name: string;
  users: DevUser[];
}

// Map a role string to an icon + color (UI affordance for quick-login buttons)
function roleStyle(role: string): { icon: typeof FiUser; color: string } {
  switch (role) {
    case 'super_admin':
      return { icon: FiShield, color: 'purple.600' };
    case 'tenant_admin':
      return { icon: FiSettings, color: 'red.600' };
    case 'manager':
      return { icon: FiTruck, color: 'green.600' };
    case 'driver':
      return { icon: FiTruck, color: 'orange.500' };
    case 'operator':
    default:
      return { icon: FiUser, color: 'blue.600' };
  }
}

interface LoginFormChakraProps {
  /** External URL to redirect to after login (must be *.axonfleet.com) */
  returnUrl?: string | null;
}

export function LoginFormChakra({ returnUrl }: LoginFormChakraProps) {
  const { login } = useAuth();
  const [_showPassword, _setShowPassword] = useState(false);
  const t = useTranslations();

  const bgCard = 'rgba(255, 255, 255, 0.95)';
  const borderColor = 'gray.200';

  // Form validation schema with translations
  const loginSchema = z.object({
    email: z.string().email('Invalid email').min(1, 'Email required'),
    password: z.string().min(1, 'Password required'),
    tenantId: z.string().optional(),
    rememberMe: z.boolean().optional(),
  });

  type LoginFormData = z.infer<typeof loginSchema>;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email:
        process.env.NODE_ENV === 'development'
          ? process.env.NEXT_PUBLIC_DEMO_EMAIL || 'qa-admin@axonfleet.com'
          : '',
      password:
        process.env.NODE_ENV === 'development'
          ? process.env.NEXT_PUBLIC_DEMO_PASSWORD || 'demo123'
          : '',
      tenantId:
        process.env.NODE_ENV === 'development'
          ? process.env.NEXT_PUBLIC_DEMO_TENANT_ID || '770e8400-e29b-41d4-a716-446655440002'
          : '',
      rememberMe: process.env.NODE_ENV === 'development' ? true : false,
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      await login({
        email: data.email,
        password: data.password,
        tenantId: data.tenantId,
        rememberMe: data.rememberMe,
        redirectPath: returnUrl || undefined,
      });
    } catch (err: unknown) {
      const error = err as Error;
      toaster.create({
        title: t('auth.loginError'),
        description: error.message || t('errors.general'),
        type: 'error',
        duration: 5000,
      });
    }
  };

  // Dev-only quick-login: fetch tenants + active users, let user pick tenant + click a user
  const isDev = process.env.NODE_ENV === 'development';

  const { data: devOptions } = useQuery<DevTenantOption[]>({
    queryKey: ['dev-quick-login-options'],
    queryFn: async () => {
      const res = await apiClient.get<DevTenantOption[]>('/auth/dev/quick-login-options');
      return res.data;
    },
    enabled: isDev,
    staleTime: 60_000,
  });

  const defaultSubdomain =
    process.env.NEXT_PUBLIC_DEMO_TENANT_SUBDOMAIN ||
    (devOptions?.find((t) => t.subdomain === 'demo') ? 'demo' : devOptions?.[0]?.subdomain) ||
    '';
  const [selectedSubdomain, setSelectedSubdomain] = useState<string>(defaultSubdomain);
  const activeSubdomain = selectedSubdomain || defaultSubdomain;
  const selectedTenant =
    devOptions?.find((t) => t.subdomain === activeSubdomain) ?? devOptions?.[0];

  const handleQuickLogin = async (user: DevUser, tenantId: string) => {
    try {
      await login({
        email: user.email,
        password: user.quickLoginPassword ?? DEMO_QUICK_LOGIN_PASSWORD,
        tenantId,
        rememberMe: true,
        redirectPath: returnUrl || undefined,
      });
    } catch (err: unknown) {
      const error = err as Error;
      toaster.create({
        title: t('auth.loginError'),
        description: error.message || t('errors.general'),
        type: 'error',
        duration: 5000,
      });
    }
  };

  return (
    <ClientOnly>
      <Box
        backgroundColor={bgCard}
        padding={8}
        borderRadius="2xl"
        boxShadow="xl"
        borderStyle="solid"
        borderColor={borderColor}
        backdropFilter="blur(10px)"
        width="full"
        maxWidth="md"
        marginX="auto"
        suppressHydrationWarning
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <VStack gap={6} align="stretch">
            {/* Header */}
            <VStack gap={3} textAlign="center">
              <Heading size="xl" color="blue.600">
                {t('auth.loginTitle')}
              </Heading>
              <Text color="fg.muted">{t('auth.loginSubtitle')}</Text>
              {isDev && devOptions && devOptions.length > 0 && (
                <Box width="full">
                  <HStack justify="space-between" marginBottom={2} width="full">
                    <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                      🚀 Quick Login (Dev Mode)
                    </Text>
                    <select
                      value={activeSubdomain}
                      onChange={(e) => setSelectedSubdomain(e.target.value)}
                      style={{
                        fontSize: '12px',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                      }}
                    >
                      {devOptions.map((t) => (
                        <option key={t.subdomain} value={t.subdomain}>
                          {t.name} ({t.subdomain})
                        </option>
                      ))}
                    </select>
                  </HStack>
                  <VStack gap={2} width="full">
                    {(selectedTenant?.users ?? []).map((user) => {
                      const primaryRole = user.roles[0] ?? 'operator';
                      const { icon: RoleIcon, color } = roleStyle(primaryRole);
                      const displayName = `${user.firstName} ${user.lastName}`.trim() || user.email;
                      return (
                        <Box
                          key={`${selectedTenant!.tenantId}:${user.email}`}
                          as="button"
                          onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleQuickLogin(user, selectedTenant!.tenantId);
                          }}
                          width="full"
                          padding={3}
                          borderRadius="lg"
                          borderWidth="1px"
                          borderColor="border.default"
                          backgroundColor="bg.subtle"
                          cursor="pointer"
                          transition="all 0.2s"
                          _hover={{
                            backgroundColor: 'bg.muted',
                            borderColor: color,
                            transform: 'translateY(-1px)',
                            boxShadow: 'sm',
                          }}
                          textAlign="left"
                        >
                          <HStack gap={3}>
                            <Box
                              padding={2}
                              borderRadius="full"
                              backgroundColor={color}
                              color="white"
                            >
                              <Icon width={4} height={4}>
                                <RoleIcon />
                              </Icon>
                            </Box>
                            <VStack gap={0} align="flex-start" flex={1}>
                              <HStack gap={2}>
                                <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                                  {displayName}
                                </Text>
                                <Text
                                  fontSize="xs"
                                  color="white"
                                  backgroundColor={color}
                                  paddingX={2}
                                  paddingY={0.5}
                                  borderRadius="full"
                                >
                                  {primaryRole}
                                </Text>
                              </HStack>
                              <Text fontSize="xs" color="fg.muted">
                                {user.email}
                              </Text>
                            </VStack>
                          </HStack>
                        </Box>
                      );
                    })}
                  </VStack>
                </Box>
              )}
              {process.env.NEXT_PUBLIC_FEATURE_DEMO_MODE === 'true' && (
                <Box
                  width="full"
                  padding={3}
                  borderRadius="md"
                  backgroundColor="blue.50"
                  borderWidth="1px"
                  borderColor="blue.200"
                >
                  <Text fontSize="xs" color="blue.700" fontWeight="medium" textAlign="center">
                    {t('demoAdmin.demoCredentials')}: demo@axonfleet.com / Axon2026!
                  </Text>
                </Box>
              )}
            </VStack>

            {/* Form Fields */}
            <VStack gap={4} width="full">
              {/* Email Field */}
              <Input
                label={t('auth.email')}
                type="email"
                placeholder="name@company.com"
                leftIcon={
                  <Icon>
                    <FiMail />
                  </Icon>
                }
                error={errors.email?.message}
                inputSize="lg"
                autoComplete="email"
                suppressHydrationWarning
                {...register('email')}
              />

              {/* Password Field */}
              <Box width="full" suppressHydrationWarning>
                <Input
                  label={t('auth.password')}
                  type={_showPassword ? 'text' : 'password'}
                  placeholder={t('auth.password')}
                  leftIcon={
                    <Icon>
                      <FiLock />
                    </Icon>
                  }
                  error={errors.password?.message}
                  inputSize="lg"
                  autoComplete="current-password"
                  suppressHydrationWarning
                  {...register('password')}
                />
              </Box>

              {/* Tenant ID Field (Hidden in development) */}
              {process.env.NODE_ENV === 'development' && (
                <Input type="hidden" {...register('tenantId')} />
              )}
            </VStack>

            {/* Remember Me & Forgot Password */}
            <HStack justifyContent="space-between" alignItems="center">
              <Controller
                name="rememberMe"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Checkbox checked={value} onChange={(checked: boolean) => onChange(checked)}>
                    {t('auth.rememberMe')}
                  </Checkbox>
                )}
              />
              <Link href="/forgot-password">
                <Text as="span" color="blue.600" fontSize="sm" fontWeight="medium" cursor="pointer">
                  {t('auth.forgotPassword')}
                </Text>
              </Link>
            </HStack>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth={true}
              isLoading={isSubmitting}
              loadingText={t('auth.signingIn')}
              leftIcon={!isSubmitting ? <FiLogIn /> : undefined}
            >
              {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
            </Button>

            {/* Social Login & Sign Up - Disabled in production */}
            {process.env.NODE_ENV !== 'production' && (
              <>
                {/* Separator */}
                <HStack alignItems="center" width="full">
                  <Box height="1px" backgroundColor="border.default" flex="1" />
                  <Text fontSize="sm" color="fg.muted" whiteSpace="nowrap" paddingX={2}>
                    {t('auth.continueWith')}
                  </Text>
                  <Box height="1px" backgroundColor="border.default" flex="1" />
                </HStack>

                {/* Social Login Buttons */}
                <HStack gap={3}>
                  <Button
                    variant="outline"
                    disabled
                    fullWidth={true}
                    leftIcon={
                      <Icon>
                        <FcGoogle />
                      </Icon>
                    }
                  >
                    {t('auth.googleSignIn')}
                  </Button>
                </HStack>

                {/* Sign Up Link */}
                <Text textAlign="center" fontSize="sm">
                  {t('auth.noAccount')}{' '}
                  <Link href="/register">
                    <Text as="span" color="blue.600" fontWeight="medium" cursor="pointer">
                      {t('auth.signUpFree')}
                    </Text>
                  </Link>
                </Text>
              </>
            )}
          </VStack>
        </form>
      </Box>
    </ClientOnly>
  );
}
